import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as gatewayActions from '../../actions/gatewayActions';
import XMRButton from '../../components/XMRButton';
import ClipLoader from 'react-spinners/ClipLoader';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  ButtonGroup,
  Pagination,
  PaginationItem,
  PaginationLink,
  Input,
  InputGroupText,
  InputGroup,
  ButtonDropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
  CardFooter,
} from 'reactstrap';
import XMTable from '../../components/XMTable';
import ListRow from './components/Logs/ListRow';
import ListDetails from './components/Logs/ListDetails';
import OperationMultiSelect from './components/Logs/OperationMultiSelect';

class Logs extends Component {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
    this.state = {
      expandId: null,
      page: 0,
      size: 10,
      integrationName: null,
      operationNames: [],
      status: null,
      integrationsToggle: false,
      statusToggle: false,
      showStoreIntegrationOperations: false,
      correlationId: null,
      integrationSearchTerm: '', // Added for integration search
      filteredIntegrations: [], // Added for filtered integrations
      message: null,
      loading: true,
      pageInputValue: '', // Local state for page input
      lastFetchedStoreId: null, // Track which storeId we last fetched logs for
    };
    this.baseState = { ...this.state };
  }

  async componentDidMount() {
    // Wait for gateway store to be loaded before fetching logs
    // Check if currentStore and currentGatewayStore match
    const { currentStore, currentGatewayStore } = this.props;
    if (currentStore && currentStore.storeId && currentGatewayStore && currentGatewayStore.storeId) {
      // Only fetch if storeIds match (ensures we're using the correct store)
      if (currentStore.storeId === currentGatewayStore.storeId) {
        await this.fetchGatewayStoreLogs();
        this.updateFilteredIntegrations();
        // Initialize page input value
        this.setState({ pageInputValue: this.state.page + 1 });
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { currentStore, currentGatewayStore } = this.props;
    const prevStoreId = prevProps.currentGatewayStore?.storeId;
    const currentStoreId = currentGatewayStore?.storeId;
    const expectedStoreId = currentStore?.storeId;
    
    // Refetch logs when storeId changes (e.g., navigating between stores)
    if (prevStoreId && currentStoreId && prevStoreId !== currentStoreId) {
      // Store changed, reset filters and refetch logs
      // Only fetch if the gateway store matches the expected store
      if (currentStoreId === expectedStoreId) {
        this.setState({ ...this.baseState, pageInputValue: 1, lastFetchedStoreId: null }, () => {
          this.updateFilteredIntegrations();
          this.fetchGatewayStoreLogs(this.baseState);
        });
      }
      return;
    }
    
    // If gateway store was just loaded and matches expected store, fetch logs
    // This handles the case where componentDidMount ran before gateway store was loaded
    if (!prevProps.currentGatewayStore?.storeId && currentStoreId && currentStoreId === expectedStoreId) {
      if (this.state.lastFetchedStoreId !== currentStoreId) {
        // Only fetch if we haven't fetched for this storeId yet (avoid duplicate calls)
        this.fetchGatewayStoreLogs();
        this.updateFilteredIntegrations();
        this.setState({ 
          pageInputValue: this.state.page + 1,
          lastFetchedStoreId: currentStoreId
        });
      }
    }
    
    // Update filtered integrations when integrations change
    if (prevProps.currentGatewayStore?.integrations !== this.props.currentGatewayStore?.integrations) {
      this.updateFilteredIntegrations();
    }
  }

  updateFilteredIntegrations = () => {
    const { integrations } = this.props.currentGatewayStore;
    const { integrationSearchTerm } = this.state;

    if (!integrations) {
      this.setState({ filteredIntegrations: [] });
      return;
    }

    const filtered = integrations.filter((integration) =>
      integration.toLowerCase().includes(integrationSearchTerm.toLowerCase())
    );

    this.setState({ filteredIntegrations: filtered });
  };

  handleIntegrationSearchChange = (e) => {
    const searchTerm = e.target.value;
    this.setState({ integrationSearchTerm: searchTerm }, () => {
      this.updateFilteredIntegrations();
    });
  };

  fetchGatewayStoreLogs = async (values) => {
    const currentFilters = {
      page: this.state.page,
      size: this.state.size,
      integrationName: this.state.integrationName,
      operationNames: this.state.operationNames,
      status: this.state.status,
      correlationId: this.state.correlationId,
    };

    const filters = Object.assign({}, currentFilters, values);
    const { dispatch, currentStore, currentGatewayStore } = this.props;

    // Use currentStore.storeId as source of truth (matches route)
    // Only proceed if gateway store matches expected store
    const expectedStoreId = currentStore?.storeId;
    const gatewayStoreId = currentGatewayStore?.storeId;
    
    if (!expectedStoreId) {
      console.warn('Cannot fetch logs: currentStore.storeId is not available');
      return;
    }
    
    if (!gatewayStoreId) {
      console.warn('Cannot fetch logs: currentGatewayStore.storeId is not available');
      return;
    }
    
    if (expectedStoreId !== gatewayStoreId) {
      console.warn(`Store mismatch: expected ${expectedStoreId}, but gateway store is ${gatewayStoreId}. Waiting for state to update...`);
      return;
    }

    try {
      this.setState({ loading: true });
      await dispatch(gatewayActions.getLogs(expectedStoreId, filters));
      // Update lastFetchedStoreId after successful fetch
      this.setState({ lastFetchedStoreId: expectedStoreId });
    } catch (error) {
      console.error('Error fetching logs:', error);
      // Error is already handled by the Redux action and reducer
    } finally {
      this.setState({ loading: false });
    }
  };

  refreshInputs = () => {
    // No longer needed since we're using controlled inputs
    // The state will automatically update the input values
  };

  refreshLogs = () => {
    // Preserve current filters and search, just refresh the data
    const currentFilters = {
      page: this.state.page,
      size: this.state.size,
      integrationName: this.state.integrationName,
      operationNames: this.state.operationNames,
      status: this.state.status,
      correlationId: this.state.correlationId,
    };
    this.fetchGatewayStoreLogs(currentFilters);
  };

  resetFilters = () => {
    // Reset all filters to base state
    this.setState({ ...this.baseState, pageInputValue: 1 }, () => {
      // After state is reset, update filtered integrations to show all
      this.updateFilteredIntegrations();
    });
    this.fetchGatewayStoreLogs(this.baseState);
    this.refreshInputs();
  };

  handleIntegrationName = (value) => {
    if (value != null) {
      const { dispatch, currentGatewayStore } = this.props;
      dispatch(gatewayActions.getStoreIntegration(currentGatewayStore.storeId, value));
      this.setState({ showStoreIntegrationOperations: true });
    } else {
      this.setState({ showStoreIntegrationOperations: false });
    }
    let filterState = { integrationName: value, page: 0, operationNames: [], pageInputValue: 1 };
    this.setState(filterState);
    this.fetchGatewayStoreLogs(filterState);
    this.refreshInputs();
  };

  handleStatusFilter = (value) => {
    let filterState = { status: value, page: 0, pageInputValue: 1 };
    this.setState(filterState);
    this.fetchGatewayStoreLogs(filterState);
  };

  handleOperationNames = (operationNames) => {
    let filterState = { operationNames: operationNames, page: 0, pageInputValue: 1 };
    this.setState(filterState);
    this.fetchGatewayStoreLogs(filterState);
  };

  handlePaginationPage = (value) => {
    if (value < 0) {
      value = 0;
    }
    this.setState({ page: value, pageInputValue: value + 1 });
    this.fetchGatewayStoreLogs({ page: value });
  };

  handlePageSizeChange = (newSize) => {
    // Reset to page 0 when changing page size
    this.setState({ size: newSize, page: 0, pageInputValue: 1 });
    this.fetchGatewayStoreLogs({ size: newSize, page: 0 });
  };

  handlePaginationSearchTerm = (value) => {
    if (value && value.length > 0 && value.length <= 2) {
      this.setState({ message: 'Please enter more than 2 characters.' });
    } else {
      this.setState({ message: null });
    }
    const filters = { correlationId: value, page: 0, pageInputValue: 1 };
    this.setState(filters);
    this.fetchGatewayStoreLogs(filters);
  };

  toggleExpand = (id) => {
    return () => {
      if (this.state.expandId !== null && this.state.expandId === id) {
        this.setState({ expandId: null });
      } else {
        this.setState({ expandId: id });
      }
    };
  };

  getSelectedIntegrationName = () => {
    return this.state.integrationName ?? 'Integrations';
  };

  getSelectedStatusFilter = () => {
    const statusLabels = {
      SUCCESS: 'Success',
      ERROR: 'Error',
      PENDING: 'Pending',
      all: 'All Status',
    };
    return statusLabels[this.state.status] ?? 'All Status';
  };

  showStoreIntegrationOperations = () => {
    return this.state.showStoreIntegrationOperations;
  };

  render() {
    const { gatewayLogs, pagination, currentGatewayStore, storeIntegration, error } = this.props;
    const { integrations } = currentGatewayStore;
    return (
      <Card>
        <CardBody>
          {/* Filters Section */}
          <Card className="mb-3">
            <CardBody className="py-3">
              <Row className="align-items-center">
                {/* Correlation ID Search */}
                <Col md="3">
                  <div className="d-flex align-items-center">
                    <label className="form-label me-2 mb-0" style={{ minWidth: '100px' }}>
                      <strong>Correlation ID:</strong>
                    </label>
                    <InputGroup size="sm">
                      <Input
                        id="correlationId"
                        type="text"
                        name="correlationId"
                        placeholder="Search by Correlation ID..."
                        minLength={3}
                        value={this.state.correlationId || ''}
                        onChange={(e) => {
                          this.setState({ correlationId: e.target.value });
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            this.handlePaginationSearchTerm(e.target.value);
                          }
                        }}
                      />
                      <Button
                        color="outline-secondary"
                        onClick={() => {
                          this.handlePaginationSearchTerm(this.state.correlationId);
                        }}
                        disabled={this.state.loading}
                        title="Search"
                      >
                        <i className="fa-solid fa-magnifying-glass"></i>
                      </Button>
                    </InputGroup>
                  </div>
                </Col>

                {/* Integrations Filter */}
                <Col md="3">
                  <div className="d-flex align-items-center">
                    <label className="form-label me-2 mb-0" style={{ minWidth: '100px' }}>
                      <strong>Integration:</strong>
                    </label>
                    {integrations && (
                      <ButtonDropdown
                        id="Integrations"
                        isOpen={this.state.integrationsToggle}
                        toggle={() => {
                          const newToggleState = !this.state.integrationsToggle;
                          this.setState({ integrationsToggle: newToggleState });
                          // Clear search term when closing dropdown
                          if (!newToggleState) {
                            this.setState({ integrationSearchTerm: '' }, () => {
                              this.updateFilteredIntegrations();
                            });
                          }
                        }}
                        disabled={this.state.loading}
                      >
                        <DropdownToggle caret className="btn-sm" disabled={this.state.loading}>
                          {this.getSelectedIntegrationName()}
                        </DropdownToggle>
                        <DropdownMenu left="true" style={{ minWidth: '250px' }}>
                          <div className="p-2">
                            <Input
                              type="text"
                              placeholder="Type to search integrations..."
                              value={this.state.integrationSearchTerm}
                              onChange={this.handleIntegrationSearchChange}
                              onClick={(e) => e.stopPropagation()}
                              className="form-control form-control-sm"
                            />
                          </div>
                          <DropdownItem divider />
                          <DropdownItem
                            key="integration-all"
                            id="integration-all"
                            onClick={() => this.handleIntegrationName(null)}
                          >
                            All
                          </DropdownItem>
                          {this.state.filteredIntegrations.length === 0 ? (
                            <DropdownItem disabled>No integrations found</DropdownItem>
                          ) : (
                            this.state.filteredIntegrations.map((integration, i) => {
                              return (
                                <DropdownItem
                                  key={i}
                                  id={integration}
                                  onClick={() => this.handleIntegrationName(integration)}
                                >
                                  {integration}
                                </DropdownItem>
                              );
                            })
                          )}
                        </DropdownMenu>
                      </ButtonDropdown>
                    )}
                  </div>
                </Col>

                {/* Operations Filter */}
                <Col md="3">
                  {this.showStoreIntegrationOperations() && storeIntegration && storeIntegration.operations != null ? (
                    <div className="d-flex align-items-center">
                      <label className="form-label me-2 mb-0" style={{ minWidth: '100px' }}>
                        <strong>Operations:</strong>
                      </label>
                      <div className="flex-grow-1">
                        <OperationMultiSelect
                          selectedOperations={this.state.operationNames}
                          onOperationChange={this.handleOperationNames}
                          storeIntegration={storeIntegration}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center">
                      <label className="form-label me-2 mb-0" style={{ minWidth: '100px' }}>
                        <strong>Operations:</strong>
                      </label>
                      <div className="text-muted small">Select an integration first</div>
                    </div>
                  )}
                </Col>

                {/* Status Filter */}
                <Col md="2">
                  <div className="d-flex align-items-center">
                    <label className="form-label me-2 mb-0" style={{ minWidth: '100px' }}>
                      <strong>Status:</strong>
                    </label>
                    <ButtonDropdown
                      id="StatusFilter"
                      isOpen={this.state.statusToggle}
                      toggle={() => {
                        this.setState({ statusToggle: !this.state.statusToggle });
                      }}
                      disabled={this.state.loading}
                    >
                      <DropdownToggle caret className="btn-sm" disabled={this.state.loading}>
                        {this.getSelectedStatusFilter()}
                      </DropdownToggle>
                      <DropdownMenu left="true">
                        <DropdownItem key="status-all" id="status-all" onClick={() => this.handleStatusFilter(null)}>
                          All Status
                        </DropdownItem>
                        <DropdownItem
                          key="status-success"
                          id="status-success"
                          onClick={() => this.handleStatusFilter('SUCCESS')}
                        >
                          Success
                        </DropdownItem>
                        <DropdownItem
                          key="status-error"
                          id="status-error"
                          onClick={() => this.handleStatusFilter('ERROR')}
                        >
                          Error
                        </DropdownItem>
                        <DropdownItem
                          key="status-pending"
                          id="status-pending"
                          onClick={() => this.handleStatusFilter('PENDING')}
                        >
                          Pending
                        </DropdownItem>
                      </DropdownMenu>
                    </ButtonDropdown>
                  </div>
                </Col>

                {/* Reset Filters Button */}
                <Col md="1" className="text-end">
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={this.resetFilters}
                    disabled={this.state.loading}
                    title="Reset Filters"
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {pagination && (
            <nav>
              <Pagination>
                <PaginationItem>
                  <PaginationLink
                    previous
                    disabled={pagination.page == 1}
                    first={pagination.page == 1}
                    last={!pagination.hasNextPage}
                    onClick={() => {
                      this.handlePaginationPage(this.state.page - 1);
                    }}
                  >
                    Prev
                  </PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink
                    next
                    disabled={!pagination.hasNextPage}
                    first={pagination.page == 1}
                    last={!pagination.hasNextPage}
                    onClick={() => {
                      this.handlePaginationPage(this.state.page + 1);
                    }}
                  >
                    Next
                  </PaginationLink>
                </PaginationItem>

                <PaginationItem>
                  <InputGroup>
                    <InputGroupText>Page</InputGroupText>
                    <Input
                      id="pageInput"
                      type="number"
                      name="page"
                      style={{ width: '80px' }}
                      min="1"
                      value={this.state.pageInputValue}
                      onChange={(e) => {
                        this.setState({ pageInputValue: e.target.value });
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          this.handlePaginationPage(e.target.value - 1);
                        }
                      }}
                    />
                    <Button
                      color="outline-secondary"
                      onClick={() => {
                        const value = parseInt(this.state.pageInputValue) - 1;
                        this.handlePaginationPage(value);
                      }}
                      disabled={this.state.loading}
                      title="Go to Page"
                    >
                      Go
                    </Button>
                  </InputGroup>
                </PaginationItem>
                <PaginationItem>
                  <ButtonGroup>
                    <XMRButton loading={this.state.loading} handleRefresh={() => this.refreshLogs()} />
                  </ButtonGroup>
                </PaginationItem>
                {this.state.loading && (
                  <PaginationItem>
                    <ButtonGroup>
                      <Button className="btn btn-light">
                        <ClipLoader size={16} color={'#3e515b'} loading={true} />
                      </Button>
                    </ButtonGroup>
                  </PaginationItem>
                )}
              </Pagination>

              <Row>
                <Col md="12">
                  {gatewayLogs && !error && (
                    <div className="float-end text-primary">
                      <span className="float-end text-primary me-2">
                        Per page:
                        <select
                          value={this.state.size}
                          onChange={(e) => this.handlePageSizeChange(parseInt(e.target.value))}
                          className="form-select form-select-sm d-inline-block ms-1"
                          style={{ width: 'auto' }}
                          disabled={this.state.loading}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        logs
                      </span>
                      <span className="float-end text-primary me-2">-</span>
                      <span>
                        Current page: <b>{pagination.page}</b>
                      </span>
                    </div>
                  )}
                </Col>
              </Row>
            </nav>
          )}
          {this.state.loading ? (
            <div className="text-center py-5">
              <ClipLoader size={36} color={'#3e515b'} loading={true} />
              <div className="mt-3 text-muted">Loading logs...</div>
            </div>
          ) : (
            gatewayLogs && (
              <XMTable
                autoRenderRow={false}
                headers={[
                  { name: 'Details' },
                  { name: 'Created At' },
                  { name: 'Requested At' },
                  { name: 'Duration' },
                  { name: 'Correlation ID' },
                  {
                    name: 'Integration',
                  },
                  {
                    name: 'Operation',
                  },
                  {
                    name: 'Status Code',
                  },
                ]}
              >
                {/*
                  Keyed by exchange id, not by array position. Keying by index makes React reuse one
                  ListDetails instance for whatever exchange happens to land in that slot after a page
                  change or a refresh, so its state — the open resend dialog, the payload being edited,
                  an in-flight poll — carries over to a different exchange. Keying by id unmounts the old
                  row and mounts a fresh one instead. The expanded row is tracked by id for the same
                  reason: an index would follow the position rather than the exchange.
                */}
                {gatewayLogs.map((log) => [
                  <ListRow
                    key={`row-${log.id}`}
                    log={log}
                    expand={this.state.expandId === log.id}
                    onRowClick={this.toggleExpand(log.id)}
                  />,
                  <ListDetails key={`detail-${log.id}`} expand={this.state.expandId === log.id} logId={log.id} />,
                ])}
              </XMTable>
            )
          )}
          <CardFooter>
            {error && (
              <div>
                <label className="alert alert-danger">
                  <pre>{error}</pre>
                </label>
              </div>
            )}
            {this.state.message && (
              <div>
                <label className="alert alert-warning">{this.state.message}</label>
              </div>
            )}
            {!gatewayLogs && <label className="alert alert-info">No logs</label>}
          </CardFooter>
        </CardBody>
      </Card>
    );
  }
}

export default connect((state) => ({
  currentStore: state.store.one,
  currentGatewayStore: state.gateway.one,
  dataCentre: state.dataCentre,
  gatewayLogs: state.gateway.logs,
  pagination: state.gateway.pagination,
  storeIntegration: state.gateway.storeIntegration,
  error: state.gateway.error,
}))(Logs);
